import { observer } from "mobx-react-lite";
import * as S from "./style";
import type { TestTriggerEditorOption } from "@smartsuite/automation-engine-api";
import { CircleLoaderIcon } from "@smartsuite/react-ui/lib/components/loaders/CircleLoaderIcon/CircleLoaderIcon";
import {
  Button,
  buttonDefaultStyles,
  Icon,
  KeyValueCustomSelect,
  Text,
  TextSizes,
} from "@smartsuite/react-ui/lib";
import { useEffect, useState } from "react";
import { RadioOption } from "@smartsuite/react-ui/lib/components/selects/components/RadioOption/RadioOption";
import { getIntegrationLogo } from "../../../../../../../../utils/integration-logo";
import { Colors } from "@smartsuite/types";
import { translateIfPresent } from "../../../../../../../../locales/i18n";
import { formatValueConfigToStructure } from "../../../../utils";
import { formatDate } from "./date";
import { checkDepthOfConfig, checkIfConfigIncludeListOfList } from "./utils";
import { useTranslation } from "react-i18next";

const buttonStyles = {
  ...buttonDefaultStyles,
  height: 32,
  sidePadding: 15,
  borderRadius: 8,
  textSize: TextSizes.L,
};

const MAX_DEPTH = 10;

export const RequestList = observer<{
  onChange: (value: string) => void;
  onRefresh: () => void;
  options: TestTriggerEditorOption[];
  loading: boolean;
  integrationId: string;
  timezone: string;
}>(({ onChange, onRefresh, options, loading, integrationId, timezone }) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<
    TestTriggerEditorOption | undefined
  >();
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [optionError, setOptionError] = useState<string | undefined>();
  const [clickRefresh, setClickRefresh] = useState(false);

  const onSelectedOptionChange = (value: string): void => {
    const option = options.find((o) => o.value === value);
    setSelectedOption(option);
  };

  const onRefreshClick = (): void => {
    setClickRefresh(true);
    onRefresh();
  };

  useEffect(() => {
    if (selectedOption?.config !== undefined) {
      if (checkIfConfigIncludeListOfList(selectedOption.config)) {
        setOptionError(
          translateIfPresent(
            "triggers.Nested arrays (arrays within arrays) are not currently supported. Please choose a different structure.",
            "Nested arrays (arrays within arrays) are not currently supported. Please choose a different structure.",
          ),
        );
      } else if (checkDepthOfConfig(selectedOption.config) > MAX_DEPTH) {
        setOptionError(
          t("triggers.The selected structure is too deep", {
            depth: MAX_DEPTH,
          }),
        );
      } else {
        setOptionError(undefined);
      }
    }
  }, [selectedOption]);

  const LogoIcon = getIntegrationLogo(integrationId);

  return (
    <>
      {options.length === 0 ? (
        <S.StyledListeningCard>
          <S.StyledListeningText>
            {clickRefresh && !loading ? (
              <>
                <S.StyledRequestListHeader>
                  {translateIfPresent(
                    "triggers.No Request found yet!",
                    "No Request found yet!",
                  )}
                </S.StyledRequestListHeader>
                <S.StyledRequestListSubHeader>
                  {translateIfPresent(
                    "triggers.Check your application preferences and try again.",
                    "Check your application preferences and try again.",
                  )}
                </S.StyledRequestListSubHeader>
              </>
            ) : (
              <>
                <S.StyledRequestListHeader>
                  {translateIfPresent(
                    "triggers.We are listening!",
                    "We are listening!",
                  )}
                </S.StyledRequestListHeader>
                <S.StyledRequestListSubHeader>
                  {translateIfPresent(
                    "triggers.Have your application send a request to the webhook URL and then test your trigger.",
                    "Have your application send a request to the webhook URL and then test your trigger.",
                  )}
                </S.StyledRequestListSubHeader>
              </>
            )}
          </S.StyledListeningText>

          <Button
            onClick={onRefreshClick}
            loading={loading}
            styles={buttonStyles}
          >
            {translateIfPresent("triggers.Test Trigger", "Test Trigger")}
          </Button>
        </S.StyledListeningCard>
      ) : (
        <S.StyledRequestList>
          <S.StyledRequestListHeader>
            {translateIfPresent(
              "triggers.We found a request!",
              "We found a request!",
            )}
          </S.StyledRequestListHeader>
          <S.StyledRequestListSubHeader>
            {translateIfPresent(
              "triggers.We’ve found the following requests.",
              "We’ve found the following requests.",
            )}{" "}
            <S.StyledRequestListRefreshBtn onClick={onRefresh}>
              {translateIfPresent("general.Refresh", "Refresh")}
            </S.StyledRequestListRefreshBtn>
            {loading && <CircleLoaderIcon />}
          </S.StyledRequestListSubHeader>
          <S.StyledRequestListSelect>
            <KeyValueCustomSelect
              state={isDropdownOpened ? "active" : "default"}
              showClear={false}
              value={selectedOption?.value}
              placeholder={translateIfPresent(
                "triggers.Select a request",
                "Select a request",
              )}
              options={options.map((o) => ({
                value: o.value,
                label: o.label,
                timestamp: o.timestamp,
              }))}
              onChange={(value) => onSelectedOptionChange(String(value))}
              renderOption={(item) => (
                <S.DropdownOption>
                  <RadioOption isActive={item.value === selectedOption?.value}>
                    <S.RadioOptionLabel>{item.label}</S.RadioOptionLabel>
                    {item.timestamp && (
                      <S.RadioOptionSubLabel>
                        {translateIfPresent("general.Pulled in", "Pulled in")}{" "}
                        {formatDate(+item.timestamp.seconds, timezone)}
                      </S.RadioOptionSubLabel>
                    )}
                  </RadioOption>
                </S.DropdownOption>
              )}
              renderSelectedOption={(item) => (
                <S.DropdownOption>
                  <LogoIcon />
                  <Text>{item.label}</Text>
                </S.DropdownOption>
              )}
              onOpenedChange={setIsDropdownOpened}
            />
          </S.StyledRequestListSelect>
          {selectedOption && (
            <S.StyledRequestSelectedItem>
              {formatValueConfigToStructure(selectedOption.config).map((v) => (
                <Text key={v}>{v}</Text>
              ))}
              <Button
                onClick={() => onChange(selectedOption?.value)}
                styles={buttonStyles}
                disabled={loading || !!optionError}
              >
                {translateIfPresent(
                  "triggers.Use this structure",
                  "Use this structure",
                )}
              </Button>
              {optionError && (
                <S.StyledRequestListError>
                  {optionError}
                </S.StyledRequestListError>
              )}
            </S.StyledRequestSelectedItem>
          )}
        </S.StyledRequestList>
      )}
      <S.StyledInfoSection>
        <S.StyledInfoSectionIcon>
          <Icon name={"info-baloon"} color={Colors.custom16} />
        </S.StyledInfoSectionIcon>
        <Text>
          {translateIfPresent(
            "triggers.Remember that anyone who has the URL for your webhook trigger can trigger your automation. We recommend thinking carefully about how and where this URL might be shared.",
            "Remember that anyone who has the URL for your webhook trigger can trigger your automation. We recommend thinking carefully about how and where this URL might be shared.",
          )}
        </Text>
      </S.StyledInfoSection>
    </>
  );
});
